import { useEffect, useReducer, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import localForage from 'localforage';
import { getCorporationsAPI, corporateLoginAPI, fetchCorporationDetailAPI } from '@/api/corporate';
import AppHeader from '@/layouts/AppHeader';
import BaseCard from '@/components/ui/BaseCard';
import BaseButton from '@/components/ui/BaseButton';
import BaseAlert from '@/components/feedback/BaseAlert';
import { MenuItem, Select, InputLabel, FormControl, CircularProgress, SelectChangeEvent } from '@mui/material';
import { Corporation as CorporationType } from '@/types';
import { decodeToken, encodeToken } from '@/utils/tokenUtils';
import { useAppContext } from '@/provider/contexts';
import { Logout } from '@mui/icons-material';
import BusinessIcon from '@mui/icons-material/Business';
import '@/styles/pages/CorporationSelectionPage/CorporationSelectionPage.scss';
import { performLogout } from '@/services/logoutService';
import { useTranslation } from 'react-i18next';
import { SyncSessionService } from '@/services/syncSessionService';
import { getDefaultRedirectPath } from '@/utils/deviceUtils';

interface Corporation {
    id: number;
    name: string;
}

type State = {
    hasCorpSelectionBlock: boolean;
    corporations: Corporation[];
    selectedCorporation: number | null;
    loading: boolean;
    error: string | null;
    showAlert: boolean;
};

type Action =
    | { type: 'SET_HAS_CORP_SELECTION_BLOCK'; payload: boolean }
    | { type: 'SET_CORPORATIONS'; payload: Corporation[] }
    | { type: 'SET_SELECTED_CORPORATION'; payload: number | null }
    | { type: 'SET_LOADING'; payload: boolean }
    | { type: 'SET_ERROR'; payload: { error: string; showAlert: boolean } }
    | { type: 'SET_SHOW_ALERT'; payload: boolean };

const initialState: State = {
    hasCorpSelectionBlock: false,
    corporations: [],
    selectedCorporation: null,
    loading: false,
    error: null,
    showAlert: false,
};

const reducer = (state: State, action: Action): State => {
    switch (action.type) {
        case 'SET_HAS_CORP_SELECTION_BLOCK':
            return { ...state, hasCorpSelectionBlock: action.payload };
        case 'SET_CORPORATIONS':
            return {
                ...state,
                hasCorpSelectionBlock: true,
                corporations: action.payload,
            };
        case 'SET_SELECTED_CORPORATION':
            return { ...state, selectedCorporation: action.payload };
        case 'SET_LOADING':
            return { ...state, loading: action.payload };
        case 'SET_ERROR':
            return {
                ...state,
                error: action.payload.error,
                showAlert: action.payload.showAlert,
            };
        case 'SET_SHOW_ALERT':
            return { ...state, showAlert: action.payload };
        default:
            return state;
    }
};

export default function CorporationSelectionPage() {
    const { t } = useTranslation('common');
    const [state, dispatch] = useReducer(reducer, initialState);
    const {
        setCorporationDetails,
        setNotification,
        setUserPermission,
        setUserDetails,
        setManageStoreId,
        setManageStoreName,
    } = useAppContext();
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const checkAuthentication = async () => {
        const token = await localForage.getItem('login_token');
        if (!token) {
            setNotification({
                severity: 'error',
                message: t('Your session has expired. Please log in again to continue.'),
            });
            navigate('/login', { replace: true });
        }
    };

    const fetchCorporations = async () => {
        try {
            dispatch({ type: 'SET_LOADING', payload: true });
            dispatch({ type: 'SET_SHOW_ALERT', payload: false });

            const response = await getCorporationsAPI();

            const corporationsList = response.data.aaData.map((corp: CorporationType) => ({
                id: corp.corporate_id,
                name: corp.corporate_name,
            }));

            if (response.data.aaData.length === 1) {
                dispatch({
                    type: 'SET_HAS_CORP_SELECTION_BLOCK',
                    payload: false,
                });
                const corporationId = response.data.aaData[0].corporate_id;
                await setCorporationId(corporationId);
                await handleLoginToCorporation(corporationId);
            } else {
                dispatch({
                    type: 'SET_CORPORATIONS',
                    payload: corporationsList,
                });
            }
        } catch (err) {
            console.error('Error fetching corporations:', err);
            dispatch({
                type: 'SET_ERROR',
                payload: { error: t('Failed to fetch corporations. Please try again.'), showAlert: true },
            });
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    };

    const setCorporationId = async (corporationId: number) => {
        dispatch({
            type: 'SET_SELECTED_CORPORATION',
            payload: corporationId,
        });
        localForage.setItem('corporation_id', corporationId);
    };

    const handleCorporationChange = async (event: SelectChangeEvent<number>) => {
        dispatch({
            type: 'SET_SELECTED_CORPORATION',
            payload: Number(event.target.value),
        });
        await localForage.setItem('corporation_id', Number(event.target.value));
    };

    const handleLoginToCorporation = async (selectCorporationId: number) => {
        try {
            if (selectCorporationId === null) {
                dispatch({
                    type: 'SET_ERROR',
                    payload: { error: t('Please select a corporation.'), showAlert: true },
                });
                return;
            }

            dispatch({ type: 'SET_LOADING', payload: true });
            dispatch({ type: 'SET_SHOW_ALERT', payload: false });

            const {
                data: { data },
            } = await corporateLoginAPI({
                corporate_id: selectCorporationId,
            });
            await localForage.setItem('corporate_token', encodeToken(data.token));

            localForage.setItem('hasCorporateSession', 'true');
            const corporationDetails = {
                ...data,
            };
            setCorporationDetails(corporationDetails);
            localStorage.setItem('corporationDetails', JSON.stringify(corporationDetails));
            localStorage.setItem('hasMultiCorporation', String(state.corporations.length > 1));
            setUserPermission(data.user_abilities);

            // const { data: { data: userInfo} } = await getUserInfoAPI();
            // setUserPermission(userInfo.user_abilities);

            // set corporation data in indexDB
            const detailResponse = await fetchCorporationDetailAPI(selectCorporationId);

            if (detailResponse.status === 200) {
                await localForage.setItem('corporation_details', detailResponse.data.data);
                SyncSessionService.postMessage({
                    type: 'switch',
                    data: corporationDetails,
                });
            }

            // Clear store selection so store-dependent pages prompt to pick a store after switching corporation
            setManageStoreId(null);
            setManageStoreName(null);

            setNotification({ severity: 'success', message: t('Logged in to the corporation successfully.') });

            const params = new URLSearchParams(location.search);
            const redirectTo = params.get('redirectTo');
            if (redirectTo) {
                return navigate(redirectTo, { replace: true });
            }
            return navigate(getDefaultRedirectPath(), { replace: true });
        } catch {
            dispatch({
                type: 'SET_ERROR',
                payload: { error: t('Failed to log in to the corporation. Please try again.'), showAlert: true },
            });
            setNotification({
                severity: 'error',
                message: t('Failed to log in to the corporation. Please try again.'),
            });
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    };

    const logoutHandler = async () => {
        setLoading(true);
        try {
            const rawCorp = await localForage.getItem<string>('corporate_token');
            const decoded = rawCorp ? decodeToken(rawCorp) : undefined;
            await performLogout(decoded ? [decoded] : []);

            clearUserAndCorporationDetails();

            setNotification({ severity: 'success', message: t('Logout successful!') });
            navigate('/login', { replace: true });
        } catch {
            setNotification({ severity: 'error', message: t('Logout failed. Please try again.') });
        } finally {
            setLoading(false);
        }
    };

    const clearUserAndCorporationDetails = () => {
        setUserDetails({
            token: '',
            username: t('Guest'),
            user_level: 0,
            user_abilities: [],
            token_expires: null,
        });
        setCorporationDetails({
            token: '',
            username: t('Guest'),
            corporate_name: '',
            user_level: 0,
            user_abilities: [],
            token_expires: null,
        });
    };

    useEffect(() => {
        checkAuthentication();
        fetchCorporations();
    }, []);

    return (
        <>
            <AppHeader></AppHeader>
            {state.hasCorpSelectionBlock ? (
                <div className="corporation-selection-page">
                    <BaseCard className="corporation-selection-container">
                        <div className="header-row">
                            <BusinessIcon className="corporation-icon" />
                            <h3>{t('Select Corporation')}</h3>
                        </div>

                        {state.showAlert && state.error && <BaseAlert severity="error" message={state.error} />}
                        {state.corporations.length === 0 ? (
                            <>
                                <div className="no-corporations-warning">
                                    <p>{t('Your account does not have any associated stores/corporations yet.')}</p>
                                </div>
                                <div className="button-row">
                                    <BaseButton
                                        variant="contained"
                                        color="error"
                                        size="small"
                                        loading={loading}
                                        onClick={logoutHandler}
                                        className="logout-button"
                                        startIcon={<Logout />}
                                    >
                                        {t('Logout')}
                                    </BaseButton>
                                </div>
                            </>
                        ) : (
                            <>
                                <FormControl fullWidth variant="outlined" margin="normal">
                                    <InputLabel id="corporation-select-label">{t('Corporation')}</InputLabel>
                                    <Select
                                        labelId="corporation-select-label"
                                        value={state.selectedCorporation || ''}
                                        onChange={(e) => handleCorporationChange(e)}
                                        label={t('Corporation')}
                                    >
                                        {state.corporations.map((corp) => (
                                            <MenuItem key={corp.id} value={corp.id}>
                                                {corp.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <div className="button-row">
                                    <BaseButton
                                        className="confirm-button"
                                        onClick={() => handleLoginToCorporation(state.selectedCorporation as number)}
                                        disabled={!state.selectedCorporation || state.loading}
                                    >
                                        {state.loading ? <CircularProgress size={24} /> : t('CONFIRM')}
                                    </BaseButton>
                                </div>
                            </>
                        )}
                    </BaseCard>
                </div>
            ) : (
                <div className="corporation-selection-page-loading">
                    <div className="loading-container">
                        <CircularProgress size={40} sx={{ mb: 2 }} />
                        <div>{t('Log in to the corporation...')}</div>
                    </div>
                </div>
            )}
        </>
    );
}
